import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef
} from '@angular/core';
import { NzSafeAny } from 'ng-zorro-antd/core/types';
import { Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';

import { MCell } from './cell';
import { MTable } from './table';
import { MTableService } from './table.service';
import { IMCellClickEvent, IMCellIndex, IMCellTextChange, IMTable, IMTableHeadOperation, IMTableOperation } from './typings';
import { getCellIndexFromEle, UN_REACHABLE_CELL } from './utils';

const defaultTableJson: IMTable = {
  cells: [],
  table_id: '',
  mask: [],
  text_matrix: []
};

@Component({
  selector: 'm-table',
  template: `
    <table class="m-table" (click)="tableClick$.next($event)">
      <tr
        class="m-table-tr"
        [ngClass]="{ 'm-table-tr-pinto': mIsPinto }"
        *ngFor="let cellRow of mTable.cells.slice(startIndex, endIndex); index as rowIndex"
      >
        <ng-container *ngFor="let cell of cellRow; index as colIndex">
          <ng-template #cellPopoverTpl>
            <ng-container *ngTemplateOutlet="mCellPopover; context: cellPopoverContext(cell)"></ng-container>
          </ng-template>
          <ng-template #cellContent>
            <div
              [ngClass]="{ 'm-table-td-div-text': mCellShrink }"
              [attr.data-row-index]="rowIndex + startIndex"
              [attr.data-col-index]="colIndex"
            >
              {{ cell.text }}
            </div>
            <ng-container *ngIf="operationVisible(cell, { colIndex: colIndex, rowIndex: rowIndex + startIndex })">
              <span
                nz-dropdown
                nzTrigger="click"
                [nzDropdownMenu]="menu"
                nzPlacement="bottomRight"
                class="m-table-td-icon"
                (click)="$event.stopPropagation()"
              >
                <i nz-icon>
                  <svg
                    t="1634639603389"
                    class="icon"
                    viewBox="0 0 1024 1024"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    p-id="1740"
                    width="16"
                    height="16"
                  >
                    <path
                      d="M32 224h960v-64H32v64z m0 704h960v-64H32v64zM512 704h448V384H512v320z m64-64V448h320v192H576zM416 518.4l-256 160v-320z"
                      p-id="1741"
                    ></path>
                  </svg>
                </i>
              </span>
              <nz-dropdown-menu #menu="nzDropdownMenu">
                <ul nz-menu nzSelectable>
                  <ng-container *ngFor="let item of mOperations">
                    <li
                      class="m-table-dropdown-li"
                      nz-menu-item
                      (click)="onOperationClick(item, cell, { colIndex: colIndex, rowIndex: rowIndex + startIndex })"
                      *ngIf="onOperationVisible(item, cell, { colIndex: colIndex, rowIndex: rowIndex + startIndex })"
                    >
                      <i nz-icon [nzType]="getString(item.icon)!" nzTheme="outline"></i>
                      {{ getString(item.label) }}
                    </li>
                  </ng-container>
                </ul>
              </nz-dropdown-menu>
            </ng-container>
            <ng-container *ngIf="theadOperationVisible(cell, { colIndex: colIndex, rowIndex: rowIndex + startIndex })">
              <div
                class="m-table-header-dropdown"
                *ngIf="theadOperationVisible(cell, { colIndex: colIndex, rowIndex: rowIndex + startIndex })"
                nz-dropdown
                [nzDropdownMenu]="headerMenu"
                nzTrigger="click"
                nzPlacement="bottomRight"
                (click)="$event.stopPropagation()"
              >
                <i nz-icon class="m-table-header-dropdown-icon" nzType="caret-down" nzTheme="outline"></i>
              </div>
              <nz-dropdown-menu #headerMenu="nzDropdownMenu">
                <ul nz-menu nzSelectable>
                  <ng-container *ngFor="let item of mTheadOperations">
                    <li
                      nz-menu-item
                      (click)="onHeadOperationClick(item, { colIndex: colIndex, rowIndex: rowIndex + startIndex })"
                      *ngIf="onOperationVisible(item, cell, { colIndex: colIndex, rowIndex: rowIndex + startIndex })"
                    >
                      {{ getString(item.label) }}
                    </li>
                  </ng-container>
                </ul>
              </nz-dropdown-menu>
            </ng-container>
          </ng-template>
          <ng-container *ngIf="mCellPopoverEnable(cell)">
            <td
              #cellRef
              class="m-table-td {{ cell.classnames }}"
              [class.m-table-td-active]="isActivated(rowIndex + startIndex, colIndex) && !isEditing(cell, rowIndex + startIndex, colIndex)"
              [class.m-table-td-editing]="isEditing(cell, rowIndex + startIndex, colIndex)"
              [class.m-table-td-error]="cell.error"
              [class.m-table-td-highlight]="cell.highlight"
              [attr.data-row-index]="rowIndex + startIndex"
              [attr.data-col-index]="colIndex"
              [attr.data-text]="cell.text"
              [attr.data-highlight]="cell.highlight"
              [attr.title]="cell.text"
              nz-popover
              [nzPopoverPlacement]="mCellPopoverPlacement"
              nzNoAnimation
              [nzPopoverTitle]="undefined"
              [nzPopoverContent]="cellPopoverTpl"
              nzPopoverOverlayClassName="m-table-td-popover"
            >
              <ng-container *ngIf="!isEditing(cell, rowIndex + startIndex, colIndex)">
                <ng-container *ngTemplateOutlet="cellContent"></ng-container>
              </ng-container>
            </td>
          </ng-container>
          <ng-container *ngIf="!mCellPopoverEnable(cell)">
            <td
              #cellRef
              class="m-table-td {{ cell.classnames }}"
              [class.m-table-td-active]="isActivated(rowIndex + startIndex, colIndex) && !isEditing(cell, rowIndex + startIndex, colIndex)"
              [class.m-table-td-editing]="isEditing(cell, rowIndex + startIndex, colIndex)"
              [class.m-table-td-error]="cell.error"
              [class.m-table-td-highlight]="cell.highlight"
              [attr.data-row-index]="rowIndex + startIndex"
              [attr.data-col-index]="colIndex"
              [attr.data-text]="cell.text"
              [attr.data-highlight]="cell.highlight"
              [attr.title]="cell.text"
            >
              <ng-container *ngIf="!isEditing(cell, rowIndex + startIndex, colIndex)">
                <ng-container *ngTemplateOutlet="cellContent"></ng-container>
              </ng-container>
            </td>
          </ng-container>
        </ng-container>
      </tr>
    </table>
    <div class="m-table-pagination">
      <nz-pagination
        [(nzPageIndex)]="mPageIndex"
        [(nzPageSize)]="mPageSize"
        [nzTotal]="total"
        [nzHideOnSinglePage]="mHiddenPaginationOnSinglePage"
        nzSize="small"
      ></nz-pagination>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MTableComponent implements OnInit, OnDestroy {
  readonly tableClick$ = new Subject<MouseEvent>();

  @Input() mTable = new MTable({ ...defaultTableJson });
  @Input() mEditable = true;
  @Input() mPageSize = 100;
  @Input() mPageIndex = 1; // 从 1 开始
  @Input() mHiddenPaginationOnSinglePage = true;
  @Input() mIsPinto = true;
  @Input() mCellShrink = false;
  @Input() mOperations: IMTableOperation[] = [];
  @Input() mTheadOperations: IMTableHeadOperation[] = [];
  @Input() mCellPopover: TemplateRef<NzSafeAny> | null = null;
  @Input() mCellPopoverPlacement: string[] = ['bottom', 'top'];
  @Input() mCellPopoverEnable: (cell: MCell) => boolean = () => false;

  @Output() readonly mCellClick = new EventEmitter<IMCellClickEvent>();
  @Output() readonly mCellTextChange = new EventEmitter<IMCellTextChange>();

  // 点击元素外失焦会有一种意外失去焦点的奇怪感觉

  // @HostListener('document:mouseup', ['$event'])
  // onGlobalClick(event: MouseEvent): void {
  //   if (
  //     !this.elementRef.nativeElement.contains(event.target as HTMLElement) &&
  //     !(event.target as HTMLElement).classList.contains('m-table-dropdown-li')
  //   ) {
  //     this.setCurrentActiveCell(UN_REACHABLE_CELL);
  //     this.setCurrentEditCell(UN_REACHABLE_CELL);
  //   }
  // }

  get startIndex(): number {
    return (this.mPageIndex - 1) * this.mPageSize;
  }

  get endIndex(): number {
    return this.mPageIndex * this.mPageSize;
  }

  get total(): number {
    return this.mTable.cells.length;
  }

  currentActiveCell = { ...UN_REACHABLE_CELL };
  currentEditCell = { ...UN_REACHABLE_CELL };

  getString(str?: string | (() => string)): string | undefined {
    return typeof str !== 'function' ? str : str();
  }

  isEditing(cell: MCell, rowIndex: number, colIndex: number): boolean {
    return this.mEditable && !cell.readonly && this.currentEditCell.rowIndex === rowIndex && this.currentEditCell.colIndex === colIndex;
  }

  isActivated(rowIndex: number, colIndex: number): boolean {
    return this.currentActiveCell.rowIndex === rowIndex && this.currentActiveCell.colIndex === colIndex;
  }

  operationVisible(cell: MCell, cellIndex: IMCellIndex): boolean {
    return (
      this.isActivated(cellIndex.rowIndex, cellIndex.colIndex) &&
      this.mOperations.length > 0 &&
      this.mOperations.some(o => this.onOperationVisible(o, cell, cellIndex))
    );
  }

  theadOperationVisible(cell: MCell, cellIndex: IMCellIndex): boolean {
    return (
      // 头部操作必须限制在第一行
      cellIndex.rowIndex === 0 &&
      this.mTheadOperations.length > 0 &&
      this.mTheadOperations.some(o => this.onOperationVisible(o, cell, cellIndex))
    );
  }

  cellPopoverContext(cell: MCell): { cell: MCell } {
    return { cell };
  }

  private readonly destroy$ = new Subject<void>();

  constructor(private cdr: ChangeDetectorRef, private elementRef: ElementRef<HTMLElement>, private tableService: MTableService) {}

  ngOnInit(): void {
    this.tableClick$
      .pipe(
        map(e => e.target! as HTMLElement),
        filter(e => ['TD', 'DIV'].includes(e.nodeName)),
        map(getCellIndexFromEle),
        tap(this.emitCurrentCell),
        map(this.setCurrentCellActiveOrEdit),
        tap(this.detectChanges),
        filter(
          () =>
            this.currentActiveCell.colIndex === this.currentEditCell.colIndex &&
            this.currentActiveCell.rowIndex === this.currentEditCell.rowIndex
        ),
        tap(this.setCurrentInputCell),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.subScrollToCell();
    this.subHighlightCells();
  }

  onOperationClick(operation: IMTableOperation, cell: MCell, cellIndex: IMCellIndex): void {
    if (operation.onClick) {
      operation.onClick({
        cell: cell.toJson(),
        cellIndex: cellIndex,
        label: this.getString(operation.label)
      });
    }
  }

  onHeadOperationClick(operation: IMTableOperation, cellIndex: IMCellIndex): void {
    this.mCellTextChange.emit({
      cell: this.mTable.cells[cellIndex.rowIndex][cellIndex.colIndex].toJson(),
      cellIndex: { ...cellIndex },
      text: this.getString(operation.label) ?? ''
    });
  }

  onOperationVisible(operation: IMTableOperation, cell: MCell, cellIndex: IMCellIndex): boolean {
    if (operation.visible) {
      return operation.visible({ cell: cell.toJson(), cellIndex });
    }
    return true;
  }

  render(): void {
    this.detectChanges();
  }

  private detectChanges = (): void => {
    this.cdr.detectChanges();
  };

  private setCurrentActiveCell = (e: IMCellIndex): void => {
    this.currentActiveCell = e;
  };

  private setCurrentEditCell = (e: IMCellIndex): void => {
    this.currentEditCell = e;
  };

  private setCurrentCellActiveOrEdit = (e: IMCellIndex): void => {
    // active => edit
    if (this.isActivated(e.rowIndex, e.colIndex)) {
      this.setCurrentEditCell(e);
    } else {
      this.setCurrentActiveCell(e);
      this.setCurrentEditCell(UN_REACHABLE_CELL);
    }
  };

  private setCurrentInputCell = (): void => {
    if (this.elementRef.nativeElement.querySelector(`.m-table-td-editing`)) {
      const _textarea = this.elementRef.nativeElement.querySelector('textarea');
      _textarea?.remove();
      const _targetCell = this.elementRef.nativeElement.querySelectorAll('tr')[this.currentActiveCell.rowIndex]?.querySelectorAll('td')[
        this.currentActiveCell.colIndex
      ] as HTMLTableCellElement;
      const cellTextarea = document.createElement('textarea');
      const div = document.createElement('div');
      cellTextarea.value = _targetCell.getAttribute('title')!;
      cellTextarea.style.width = '100%';
      cellTextarea.style.height = '100%';
      cellTextarea.style.resize = 'none'; // resize 会出现高度溢出的 bug
      cellTextarea.style.position = 'absolute';
      cellTextarea.style.left = '0';
      cellTextarea.style.top = '0';
      cellTextarea.style.border = 'none';
      cellTextarea.style.outline = 'none';
      cellTextarea.style.background = 'inherit';
      cellTextarea.style.padding = '8px 16px';

      // 这里复制一个内容一模一样的 div 占位
      div.style.border = 'none';
      div.style.outline = 'none';
      div.style.opacity = '0';
      div.innerText = _targetCell.getAttribute('title') ?? '';
      div.style.pointerEvents = 'none';

      _targetCell.appendChild(div);
      _targetCell.appendChild(cellTextarea);
      this.cdr.detectChanges();
      cellTextarea.focus();
      cellTextarea.onblur = () => {
        _targetCell.removeChild(div);
        _targetCell.removeChild(cellTextarea);
        // 只有在文本确切发生变化时才会触发 mCellTextChange
        if (cellTextarea.value !== _targetCell.getAttribute('title')) {
          this.mCellTextChange.emit({
            cell: this.mTable.cells[this.currentEditCell.rowIndex][this.currentEditCell.colIndex].toJson(),
            cellIndex: { rowIndex: this.currentEditCell.rowIndex, colIndex: this.currentEditCell.colIndex },
            text: cellTextarea.value
          });
        }
        this.currentEditCell = { ...UN_REACHABLE_CELL };
        this.currentActiveCell = { ...UN_REACHABLE_CELL };
        this.cdr.detectChanges();
      };
    }
  };

  private emitCurrentCell = (e: IMCellIndex): void => {
    this.mCellClick.emit({
      cell: this.mTable.cells[e.rowIndex][e.colIndex].toJson(),
      cellIndex: e
    });
  };

  private subScrollToCell(): void {
    this.tableService.scroll$
      .pipe(
        filter(
          e => !!e && e.tableId === this.mTable.id && e.colIndex < this.mTable.cellsColCount && e.rowIndex < this.mTable.cellsRowCount
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(params => {
        const { rowIndex, colIndex } = params!;
        const _pageIndex = Math.ceil((rowIndex + 1) / this.mPageSize); // Math.ceil((200 + 1) / 200) => 3
        const _tableRowIndex = rowIndex % this.mPageSize; // (200 % 200) => 0
        this.mPageIndex = _pageIndex;
        this.cdr.detectChanges();
        const _targetRow = this.elementRef.nativeElement.querySelectorAll('tr')[_tableRowIndex] as HTMLTableRowElement;
        if (_targetRow) {
          _targetRow.querySelectorAll('td')[colIndex]?.scrollIntoView({
            block: 'center',
            inline: 'center'
          });
        }
      });
  }

  private subHighlightCells(): void {
    this.tableService.highlightCells$
      .pipe(
        filter(e => e.length > 0),
        takeUntil(this.destroy$)
      )
      .subscribe(_cells => {
        this.mTable.highlightCells(_cells.filter(e => e.tableId === this.mTable.id));
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
